import React, { useEffect, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { rtlEnable, localeSelector, hasUserRequireDataSelector } from 'domain/env';
import DraggableToastContainer from 'components/DraggableToastContainer';
import moment from 'moment';

import SmartLook from 'components/SmartLook';
import OneSignalInit from 'components/OneSignalInit';
import { AppStatusBar, ConnectionStatus, DiskSpaceStatus } from 'components/AppStatusBar';
import CssBaseline from '@mui/material/CssBaseline';
import LinearProgress from '@mui/material/LinearProgress';
import MetadataManager from 'components/MetadataManager';

import 'react-toastify/dist/ReactToastify.css';
import 'lib/toast.css';

interface AppState {
  direction: 'rtl' | 'ltr';
  locale: string;
  hasUserRequireData: boolean;
}

const mapStateToProps = (state: any): AppState => ({
  direction: rtlEnable(state) ? 'rtl' : 'ltr',
  locale: localeSelector(state),
  hasUserRequireData: hasUserRequireDataSelector(state),
});

const App: React.FC = () => {
  const { direction, locale, hasUserRequireData } = useSelector(mapStateToProps);

  useEffect(() => {
    if (document.body) {
      document.body.setAttribute('class', direction);
      document.body.setAttribute('dir', direction);
    }

    moment.locale(locale);
  }, [locale, direction]);

  return (
    hasUserRequireData && (
      <>
        <CssBaseline />
        <Suspense fallback={<LinearProgress sx={{ width: '100%' }} />}>
          <Outlet />
        </Suspense>
        <DraggableToastContainer
          autoClose={3000}
          rtl={direction === 'rtl'}
          newestOnTop
          pauseOnHover
          hideProgressBar
          position={direction === 'rtl' ? 'top-left' : 'top-right'}
        />
        <AppStatusBar />
        <ConnectionStatus />
        <DiskSpaceStatus />
        <SmartLook />
        <OneSignalInit />
        <MetadataManager />
      </>
    )
  );
};

export default App;
